/**
 * `AW1` · Cómo se muestran los valores de la tabla. Sin lógica de datos.
 *
 * Está aparte de `tabla.tsx` para que ese archivo quede con las tres decisiones
 * que lleva `guard` y nada más.
 */

import type { DocumentState } from "@contracts/knowledge/index.ts"

/**
 * El idioma con el que se formatean fechas y números.
 *
 * Fijo y no de configuración: el operador de esta herramienta es de Elige
 * Educar, en Chile. Tomarlo del navegador haría que la misma fecha se viera
 * distinta según la máquina, que es peor que una decisión explícita.
 *
 * La ZONA HORARIA, en cambio, se deja en la del navegador a propósito: fijarla
 * mostraría la hora de Santiago a alguien que está en otro huso y cree estar
 * leyendo la suya.
 */
const LOCALE = "es-CL"

const FORMATO_FECHA = new Intl.DateTimeFormat(LOCALE, {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
})

const FORMATO_NUMERO = new Intl.NumberFormat(LOCALE)

/**
 * Lo que se muestra cuando el valor legítimamente no está.
 *
 * Un guión y no una celda vacía: una celda vacía se lee como «se rompió algo»,
 * y estos dos casos son normales. `topic` puede ir vacío —«en KAI puede ir
 * vacío», dice el PRD— y `fragmentCount` está en `null` hasta que el documento
 * llega a `indexado`.
 */
export const SIN_VALOR = "—"

/**
 * La fecha de carga.
 *
 * Una fecha inválida se muestra como tal en vez de romper la fila. Llega así
 * sólo si el borde mandó algo que no es una fecha, y esconderlo detrás de un
 * guión lo haría indistinguible de un campo vacío legítimo.
 */
export function formatearFecha(fecha: Date): string {
  return Number.isNaN(fecha.getTime()) ? "fecha inválida" : FORMATO_FECHA.format(fecha)
}

/** El tema, que el PRD deja explícitamente opcional. */
export function formatearTema(tema: string | null): string {
  return tema === null || tema.trim() === "" ? SIN_VALOR : tema
}

/**
 * El nº de fragmentos, que «se completa al indexar».
 *
 * `0` se muestra como `0` y no como guión: un documento indexado que no produjo
 * ningún fragmento es un dato real y probablemente un problema, y colapsarlo con
 * «todavía no se indexó» lo escondería justo cuando hay que verlo.
 */
export function formatearFragmentos(cantidad: number | null): string {
  return cantidad === null ? SIN_VALOR : FORMATO_NUMERO.format(cantidad)
}

/**
 * El estado del documento, EN PALABRAS.
 *
 * ⛔ La traducción tiene dueño —este archivo— y eso es el punto. `tabla.tsx` la
 * había rechazado con un motivo que sigue siendo cierto: una traducción suelta
 * termina viviendo en algún lado sin dueño, con el operador leyendo una cosa y la
 * base guardando otra. Acá tiene un solo lugar, un tipo de entrada y un examen.
 *
 * Los cuatro valores de entrada los fija `contracts/knowledge` `DocumentState`,
 * LITERALES: `subido` · `en proceso` · `indexado` · `error`.
 *
 * DE DÓNDE SALE CADA RÓTULO — porque no todos tienen la misma fuente:
 *   · `indexado` → «Listo»  — pedido TEXTUAL por el PRD (`F3-CA-23` en PRD:531 y
 *     `F5-CA-80` en PRD:899, «dice «Listo»») y dibujado así por el boceto, en el
 *     chip de «Guía de planificación.pdf» (`BancoDeContenido.dc.html:75`).
 *   · `error`    → «Error»  — el boceto, en el chip de «Acompañamiento.docx»
 *     (`BancoDeContenido.dc.html:74`, `class="estado mal"`).
 *   · `subido` y `en proceso` — ⚠️ **NINGUNA FUENTE LOS ROTULA.** El boceto no
 *     dibuja ningún documento en esos dos estados y el PRD sólo dice cómo se lee
 *     «Listo». Acá se capitaliza la palabra que el propio PRD ya usa (`F1.md:31`),
 *     que es exactamente lo que el chip hace con las otras dos. Si Producto quiere
 *     otro texto, se cambia en este `case` y en ningún otro lado.
 *
 * ✅ Esto NO contradice «el estado se muestra literal»: lo que se muestra literal
 * es el DATO (`data-estado`), que sigue crudo. Lo que cambia es el rótulo visible.
 *
 * ⚠️ El chip los pinta en mayúsculas por CSS (`estilos.css` `.banco__estado`,
 * `text-transform: uppercase`), así que «Listo» SE LEE «LISTO» — que es
 * literalmente lo que muestra el boceto.
 */
export function formatearEstado(estado: DocumentState): string {
  switch (estado) {
    case "subido":
      return "Subido"
    case "en proceso":
      return "En proceso"
    case "indexado":
      return "Listo"
    case "error":
      return "Error"
  }
}
